import { PlusOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTenantDate } from '../lib/tenant';

type LiquidNitrogenRecord = {
  id: string;
  filledAt: string;
  amountL: number;
  levelBeforePct: number | null;
  levelAfterPct: number | null;
  filledToFull: boolean;
  notes: string | null;
  createdAt: string;
  operatorName: string | null;
};
type LiquidNitrogenDevice = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  settings: {
    nominalCapacityL: number;
    lowLevelPct: number;
    version: number | null;
  } | null;
  forecast: {
    estimatedLevelPct: number | null;
    estimatedVolumeL: number | null;
    averageDailyLossL: number | null;
    daysUntilLow: number | null;
    estimatedLowAt: string | null;
    confidence: 'INSUFFICIENT' | 'LOW' | 'MEDIUM' | 'HIGH';
    lastFillAt: string | null;
    sampleCount: number;
  } | null;
  recentRecords: LiquidNitrogenRecord[];
};
type LiquidNitrogenSummary = {
  enabled: boolean;
  devices: LiquidNitrogenDevice[];
};
type FillFormValues = {
  deviceId: string;
  filledAt: Dayjs;
  amountL: number;
  levelBeforePct?: number;
  levelAfterPct?: number;
  filledToFull?: boolean;
  notes?: string;
};
type SettingsFormValues = {
  nominalCapacityL: number;
  lowLevelPct: number;
};

export function LiquidNitrogenMaintenancePage() {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState<LiquidNitrogenSummary>();
  const [loading, setLoading] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [settingsDevice, setSettingsDevice] =
    useState<LiquidNitrogenDevice>();
  const [fillForm] = Form.useForm<FillFormValues>();
  const [settingsForm] = Form.useForm<SettingsFormValues>();
  const [messageApi, holder] = message.useMessage();

  const deviceName = (device: LiquidNitrogenDevice) =>
    i18n.resolvedLanguage === 'en-US'
      ? (device.nameEn ?? device.nameZh ?? device.code)
      : (device.nameZh ?? device.nameEn ?? device.code);

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/storage/liquid-nitrogen', {
        credentials: 'include',
      });
      if (response.status === 401) {
        window.location.assign('/login');
        return;
      }
      if (!response.ok) throw new Error();
      setSummary((await response.json()) as LiquidNitrogenSummary);
    } catch {
      await messageApi.error(t('liquidNitrogen.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const records = useMemo(
    () =>
      (summary?.devices ?? [])
        .flatMap((device) =>
          device.recentRecords.map((record) => ({
            ...record,
            deviceName: deviceName(device),
          })),
        )
        .sort((a, b) => b.filledAt.localeCompare(a.filledAt)),
    [summary, i18n.resolvedLanguage],
  );

  const openFill = () => {
    fillForm.resetFields();
    fillForm.setFieldsValue({
      filledAt: dayjs(),
      filledToFull: false,
      ...(summary?.devices[0] ? { deviceId: summary.devices[0].id } : {}),
    });
    setFillOpen(true);
  };

  const saveFill = async (values: FillFormValues) => {
    const response = await fetch('/api/v1/storage/liquid-nitrogen/fill-records', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        ...values,
        filledAt: values.filledAt.format('YYYY-MM-DD'),
      }),
    });
    if (!response.ok) {
      await messageApi.error(t('liquidNitrogen.saveFailed'));
      return;
    }
    setSummary((await response.json()) as LiquidNitrogenSummary);
    setFillOpen(false);
    await messageApi.success(t('liquidNitrogen.saved'));
  };

  const openSettings = (device: LiquidNitrogenDevice) => {
    setSettingsDevice(device);
    settingsForm.resetFields();
    settingsForm.setFieldsValue({
      lowLevelPct: device.settings?.lowLevelPct ?? 20,
      ...(device.settings
        ? { nominalCapacityL: device.settings.nominalCapacityL }
        : {}),
    });
  };

  const saveSettings = async (values: SettingsFormValues) => {
    if (!settingsDevice) return;
    const response = await fetch(
      `/api/v1/storage/liquid-nitrogen/devices/${settingsDevice.id}/settings`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...values,
          ...(settingsDevice.settings?.version
            ? { version: settingsDevice.settings.version }
            : {}),
        }),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('liquidNitrogen.saveFailed'));
      return;
    }
    setSummary((await response.json()) as LiquidNitrogenSummary);
    setSettingsDevice(undefined);
    await messageApi.success(t('liquidNitrogen.saved'));
  };

  return (
    <section>
      {holder}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title={t('liquidNitrogen.deviceSettings')}
            extra={
              <Space>
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  disabled={!summary?.devices.length}
                  onClick={openFill}
                >
                  {t('liquidNitrogen.addFillRecord')}
                </Button>
                <Button
                  type="text"
                  shape="circle"
                  aria-label={t('liquidNitrogen.refresh')}
                  icon={<ReloadOutlined />}
                  loading={loading}
                  onClick={() => void load()}
                />
              </Space>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              pagination={false}
              dataSource={summary?.devices ?? []}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: t('liquidNitrogen.noDevices') }}
              columns={[
                {
                  title: t('liquidNitrogen.device'),
                  render: (_: unknown, device: LiquidNitrogenDevice) =>
                    deviceName(device),
                },
                ...(summary?.enabled
                  ? [
                      {
                        title: t('liquidNitrogen.estimate'),
                        render: (_: unknown, device: LiquidNitrogenDevice) => {
                          const level =
                            device.forecast?.estimatedLevelPct ?? null;
                          const low =
                            level !== null &&
                            device.settings !== null &&
                            level <= device.settings.lowLevelPct;
                          return level === null ? (
                            <span>{t('liquidNitrogen.insufficientData')}</span>
                          ) : (
                            <div className="liquid-nitrogen-estimate-cell">
                              <Progress
                                percent={level}
                                status={low ? 'exception' : 'normal'}
                                showInfo={false}
                              />
                              <span>{level}%</span>
                            </div>
                          );
                        },
                      },
                    ]
                  : []),
                {
                  title: t('liquidNitrogen.capacity'),
                  render: (_: unknown, device: LiquidNitrogenDevice) =>
                    device.settings
                      ? `${device.settings.nominalCapacityL} L`
                      : '-',
                },
                ...(summary?.enabled
                  ? [
                      {
                        title: t('liquidNitrogen.dailyLoss'),
                        render: (_: unknown, device: LiquidNitrogenDevice) =>
                          device.forecast === null ||
                          device.forecast.averageDailyLossL === null
                            ? '-'
                            : `${device.forecast.averageDailyLossL} L/${t(
                                'liquidNitrogen.day',
                              )}`,
                      },
                      {
                        title: t('liquidNitrogen.lastFillAt'),
                        render: (_: unknown, device: LiquidNitrogenDevice) =>
                          formatTenantDate(device.forecast?.lastFillAt ?? null),
                      },
                    ]
                  : []),
                {
                  title: t('common.actions'),
                  render: (_: unknown, device: LiquidNitrogenDevice) => (
                    <Button
                      type="link"
                      icon={<SettingOutlined />}
                      onClick={() => openSettings(device)}
                    >
                      {t('liquidNitrogen.configure')}
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
        <Col span={24}>
          <Card title={t('liquidNitrogen.fillHistory')}>
            <Table
              size="small"
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              dataSource={records}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: t('liquidNitrogen.noRecords') }}
              columns={[
                { title: t('liquidNitrogen.device'), dataIndex: 'deviceName' },
                {
                  title: t('liquidNitrogen.filledAt'),
                  render: (_: unknown, row: LiquidNitrogenRecord) =>
                    formatTenantDate(row.filledAt),
                },
                {
                  title: t('liquidNitrogen.amount'),
                  render: (_: unknown, row: LiquidNitrogenRecord) =>
                    `${row.amountL} L`,
                },
                {
                  title: t('liquidNitrogen.level'),
                  render: (_: unknown, row: LiquidNitrogenRecord) =>
                    `${row.levelBeforePct ?? '-'}% → ${
                      row.filledToFull ? '100' : row.levelAfterPct ?? '-'
                    }%`,
                },
                {
                  title: t('liquidNitrogen.operator'),
                  dataIndex: 'operatorName',
                  render: (value: string | null) => value ?? '-',
                },
                {
                  title: t('liquidNitrogen.notes'),
                  dataIndex: 'notes',
                  render: (value: string | null) => value ?? '-',
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <Modal
        title={t('liquidNitrogen.addFillRecord')}
        open={fillOpen}
        onCancel={() => setFillOpen(false)}
        onOk={() => void fillForm.submit()}
      >
        <Form form={fillForm} layout="vertical" onFinish={(values) => void saveFill(values)}>
          <Form.Item name="deviceId" label={t('liquidNitrogen.device')} rules={[{ required: true }]}>
            <Select
              options={(summary?.devices ?? []).map((device) => ({
                value: device.id,
                label: deviceName(device),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="filledAt"
            label={t('liquidNitrogen.filledAt')}
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="amountL"
            label={t('liquidNitrogen.amount')}
            rules={[{ required: true, type: 'number', min: 0.001 }]}
          >
            <InputNumber min={0.001} precision={3} addonAfter="L" style={{ width: '100%' }} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="levelBeforePct" label={t('liquidNitrogen.levelBefore')}>
                <InputNumber min={0} max={100} precision={1} addonAfter="%" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="levelAfterPct" label={t('liquidNitrogen.levelAfter')}>
                <InputNumber min={0} max={100} precision={1} addonAfter="%" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="filledToFull" valuePropName="checked">
            <Checkbox>{t('liquidNitrogen.filledToFull')}</Checkbox>
          </Form.Item>
          <Form.Item name="notes" label={t('liquidNitrogen.notes')}>
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={t('liquidNitrogen.deviceSettings')}
        open={Boolean(settingsDevice)}
        onCancel={() => setSettingsDevice(undefined)}
        onOk={() => void settingsForm.submit()}
      >
        <Form
          form={settingsForm}
          layout="vertical"
          onFinish={(values) => void saveSettings(values)}
        >
          <Form.Item
            name="nominalCapacityL"
            label={t('liquidNitrogen.nominalCapacity')}
            rules={[{ required: true, type: 'number', min: 0.001 }]}
          >
            <InputNumber min={0.001} precision={3} addonAfter="L" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="lowLevelPct"
            label={t('liquidNitrogen.lowLevel')}
            rules={[{ required: true, type: 'number', min: 0, max: 100 }]}
          >
            <InputNumber min={0} max={100} precision={1} addonAfter="%" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
}
